/**
 * 🔴 W72 STEP 4 — THE APP'S OWN PROGRESS CHART, PORTED (his note 2026-09-25 ~01:40: *"not use somewhere the graph
 * thing that we use in the ios app? that's our original anyway"*).
 *
 * This file is the TypeScript TWIN of `HybridTrainer/Services/ProgressChartPlan.swift` (and `ProgressRange`, which
 * moved there so the harness can compile it). EVERY function here is held to what the SWIFT printed over a corpus
 * (`tools/progress-corpus/build.py` → `__fixtures__/progressCorpus.json`, the W66 method): never a paraphrase with
 * hand-typed expectations. The drawing (`components/ProgressChart.tsx`) reads ONLY this module, so the numbers on
 * the web and on his phone cannot disagree.
 *
 * Every rule he decided on 2026-09-02 and 2026-08-25 lives in the Swift source's comments, with his words; they are
 * not restated here (a second statement of a rule is how two drift). What each function IS is named beside it.
 *
 * TIME: an instant is a millisecond number. Days are cut in the browser's zone (`startOfDay`), which is what the
 * phone's `Calendar.current` does in the athlete's. The corpus is emitted in UTC and vitest runs in UTC.
 */

import { PROGRESS_VIEWS, STORED_VIEWS, type ProgressViewKey } from "./progressViews";
import { bestOf, ENGINE_RULE, feeds, ruleBodyIsLoad, type OneRMRule, type OneRMSet, type WeighIn } from "./oneRepMax";
import { localDayKey } from "./days";
import { shortDate, weekdayDate } from "./weekLine";

export type Field = ProgressViewKey;
export const FIELDS: readonly Field[] = PROGRESS_VIEWS.map((v) => v.key);

export type Point = { readonly date: number; readonly value: number };

const DAY = 86_400_000;

/* ── calendar (Calendar.current, in the page's zone) ─────────────────────────────────────────── */

export function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function addDays(ms: number, n: number): number {
  const d = new Date(ms);
  d.setDate(d.getDate() + n);
  return d.getTime();
}

export function sameDay(a: number, b: number): boolean {
  return startOfDay(a) === startOfDay(b);
}

/** `dateComponents([.day], from:, to:).day` — whole days between two instants, truncated. */
export function wholeDaysBetween(from: number, to: number): number {
  return Math.trunc((to - from) / DAY);
}

/* ── the field ───────────────────────────────────────────────────────────────────────────────── */

export type Aggregation = "peak" | "total";

export function aggregation(f: Field): Aggregation {
  return f === "volume" || f === "totalReps" || f === "timeUnderTension" || f === "totalDistance" ? "total" : "peak";
}

export function isIntegral(f: Field): boolean {
  return !(f === "oneRM" || f === "topSet" || f === "volume" || f === "longestDistance" || f === "totalDistance");
}

/** W136 B2 S3r5 P2 — `Field.unit(distanceKm:)` / `perUnit`: a distance field on a `distance_km` exercise says km (the FIELD's
 *  unit, never the magnitude) and its metres are divided by 1000; every other field keeps its own unit. */
export function isDistanceField(f: Field): boolean {
  return f === "longestDistance" || f === "totalDistance";
}
export function unitFor(f: Field, distanceKm: boolean): string {
  return distanceKm && isDistanceField(f) ? "km" : fieldView(f).unit;
}
export function perUnit(f: Field, distanceKm: boolean): number {
  return distanceKm && isDistanceField(f) ? 1000 : 1;
}
/** `ProgressChartPlan.inUnit` — a series in the field's unit; the one place the division lives. */
export function inUnit(points: readonly Point[], f: Field, distanceKm: boolean): Point[] {
  const per = perUnit(f, distanceKm);
  return per === 1 ? [...points] : points.map((p) => ({ date: p.date, value: p.value / per }));
}

export function fieldView(f: Field) {
  const v = PROGRESS_VIEWS.find((x) => x.key === f);
  if (v === undefined) throw new Error(`unknown field ${f}`);
  return v;
}

export function availableFields(hasE1RM: boolean, hasWeight: boolean, hasReps: boolean, hasHold: boolean,
                                hasDistance = false, hasRounds = false): Field[] {
  const out: Field[] = [];
  if (hasE1RM) out.push("oneRM");
  if (hasWeight) out.push("topSet");
  if (hasWeight && hasReps) out.push("volume");
  if (hasReps) out.push("bestSet", "totalReps");
  if (hasHold) out.push("longestHold", "timeUnderTension");
  if (hasDistance) out.push("longestDistance", "totalDistance");
  if (hasRounds) out.push("mostRounds");
  return out;
}

/** `ProgressChartPlan.allowed` — the coach's ticks narrow what the DATA offers; never an empty menu while data exists. */
export function allowed(available: readonly Field[], progressViews: readonly string[] | null, rule: OneRMRule = ENGINE_RULE): Field[] {
  // 🔴 W125 — `ProgressChartPlan.allowed(available:progressViews:oneRM:)`: for a COACH's exercise the 1RM is the switch's, a
  // stored `oneRM` tick is ignored, and the other views narrow by the ticks as before. An engine exercise: unchanged.
  if (rule.kind === "coach") {
    const others = allowed(available.filter((f) => f !== "oneRM"), progressViews === null ? null : progressViews.filter((k) => k !== "oneRM"));
    return [...(rule.enabled && available.includes("oneRM") ? (["oneRM"] as Field[]) : []), ...others];
  }
  if (progressViews === null || progressViews.length === 0) return [...available];
  const ticked = new Set(progressViews.filter((k): k is Field => (FIELDS as readonly string[]).includes(k)));
  // W136 B2 S3r5 P2 — `ProgressChartPlan.allowed`: a view no tick can name (`STORED_VIEWS`) is never narrowed away.
  const kept = available.filter((f) => STORED_VIEWS.includes(f) && ticked.has(f));
  if (kept.length === 0) return [...available];
  return available.filter((f) => kept.includes(f) || !STORED_VIEWS.includes(f));
}

export function resolvedField(stored: Field | null, available: readonly Field[]): Field | null {
  if (stored !== null && available.includes(stored)) return stored;
  return available[0] ?? null;
}

export function emptyReason(name: string, sessions: number, available: readonly Field[], field: Field | null): string {
  const times = sessions === 1 ? "once" : `${sessions} times`;
  if (available.length === 0) {
    return `${name} has been logged ${times}, but without reps, a weight or a hold. `
      + "Log any of those and this chart draws itself.";
  }
  const others = available.filter((f) => f !== field).map((f) => fieldView(f).label);
  const list = others.length > 1
    ? `${others.slice(0, -1).join(", ")} or ${others[others.length - 1]}`
    : (others[0] ?? "another field");
  return `There is nothing to plot for ${field === null ? "this field" : fieldView(field).label} on ${name}. `
    + `Pick ${list} below.`;
}

/* ── the axes ────────────────────────────────────────────────────────────────────────────────── */

export type Domain = { readonly lower: number; readonly upper: number };

/* 🔴 W123 — the horizontal domain is `fullDomain` (below); W121's `xDomain` / `daySpan` are GONE with the Swift twin's. The
   half-day of room `daySpan` gave the ends existed so an EDGE dot was not halved; W123 puts the edge DAYS on the edges and
   moves "whole" to the drawing — the dots sit outside the plot's clip and are never cut (`ProgressChart.tsx`). */

export type LabelRule = { readonly kind: "days"; readonly stride: number } | { readonly kind: "months" };

export function labelRule(rangeDays: number | null, spanDays: number): LabelRule {
  switch (rangeDays) {
    case 7: return { kind: "days", stride: 1 };
    case 14: return { kind: "days", stride: 2 };
    case 30: return { kind: "days", stride: 3 };
    case 90: return { kind: "days", stride: 14 };
    case 180: case 365: return { kind: "months" };
    case null: return spanDays > 62 ? { kind: "months" } : { kind: "days", stride: Math.max(1, Math.ceil(spanDays / 8)) };
    default: return { kind: "days", stride: Math.max(1, Math.ceil(rangeDays / 8)) };
  }
}

export function labelDates(domain: Domain, rule: LabelRule): number[] {
  return rule.kind === "days" ? dayLabelDates(domain, rule.stride) : monthLabelDates(domain);
}

export function monthLabelDates(domain: Domain): number[] {
  const out: number[] = [];
  const lower = new Date(domain.lower);
  let mid = new Date(lower.getFullYear(), lower.getMonth(), 15, 12, 0, 0, 0).getTime();
  while (mid <= domain.upper) {
    if (mid >= domain.lower && mid <= domain.upper) out.push(mid);
    const d = new Date(mid);
    mid = new Date(d.getFullYear(), d.getMonth() + 1, 15, 12, 0, 0, 0).getTime();
  }
  return out;
}

/** W123 — the twin of `ProgressChartPlan.labelDates(in:strideDays:)`: a day label sits ON its day's position (midnight),
 *  counted back from the RIGHT end every `strideDays` (the last day always labelled), the first day added when the stride
 *  misses it. */
export function dayLabelDates(domain: Domain, strideDays: number): number[] {
  const step = Math.max(1, strideDays);
  const first = startOfDay(domain.lower);
  const out: number[] = [];
  for (let day = startOfDay(domain.upper); day >= first; day = addDays(day, -step)) out.push(day);
  if (out[out.length - 1] !== first && first >= domain.lower) out.push(first);
  return out.reverse();
}

export type LabelAnchor = "leading" | "center" | "trailing";
export type AxisLabel = { readonly date: number; readonly anchor: LabelAnchor };

/** The twin of `ProgressChartPlan.labelWidth`: ~6.4 px a character at the axis's 11-px face, a month name 22. */
export function labelWidth(date: number, rule: LabelRule): number {
  if (rule.kind === "months") return 22;
  const d = new Date(date);
  return (String(d.getDate()).length + String(d.getMonth() + 1).length + 1) * 6.4;
}

/** W123 (D) — the twin of `ProgressChartPlan.axisLabels`: the label ON the window's left edge hangs leading, the one ON its
 *  right edge trailing, the rest centred; a centred label crossing an edge is dropped (F2); a centred neighbour that would
 *  touch an END label (within 2 px) yields. A null width keeps every label. */
export function axisLabels(labels: readonly number[], visible: Domain, plotWidth: number | null, rule: LabelRule): AxisLabel[] {
  const anchor = (d: number): LabelAnchor => (d === visible.lower ? "leading" : d === visible.upper ? "trailing" : "center");
  const inside = labels.filter((d) => d >= visible.lower && d <= visible.upper);
  if (plotWidth === null || plotWidth <= 0) return inside.map((d) => ({ date: d, anchor: anchor(d) }));
  const span = visible.upper - visible.lower;
  const x = (d: number) => (span > 0 ? ((d - visible.lower) / span) * plotWidth : 0);
  const gap = 2;
  const left = inside.includes(visible.lower) ? labelWidth(visible.lower, rule) : null;
  const right = inside.includes(visible.upper) ? labelWidth(visible.upper, rule) : null;
  const out: AxisLabel[] = [];
  for (const d of inside) {
    const a = anchor(d);
    if (a !== "center") { out.push({ date: d, anchor: a }); continue; }
    const half = labelWidth(d, rule) / 2;
    const from = x(d) - half, to = x(d) + half;
    if (from < 0 || to > plotWidth) continue;
    if (left !== null && from < left + gap) continue;
    if (right !== null && to > plotWidth - right - gap) continue;
    out.push({ date: d, anchor: a });
  }
  return out;
}

export function yDomain(values: readonly number[], integral = false, padFraction = 0.12): Domain {
  if (values.length === 0) return { lower: 0, upper: 1 };
  const lo = Math.min(...values), hi = Math.max(...values);
  let low: number, high: number;
  if (lo === hi) {
    const pad = Math.max(Math.abs(lo) * padFraction, 1);
    low = lo - pad; high = hi + pad;
  } else {
    const pad = (hi - lo) * padFraction;
    low = lo - pad; high = hi + pad;
  }
  if (!integral) return { lower: low, upper: high };
  low = Math.floor(low);
  high = Math.ceil(high);
  if (high - low < 2) { low -= 1; high += 1; }
  return { lower: low, upper: high };
}

/** Swift's `.rounded()` — to nearest, ties AWAY from zero (JS `Math.round` ties toward +∞). */
function roundAway(x: number): number {
  return Math.sign(x) * Math.round(Math.abs(x));
}

export function yTickCount(domain: Domain, integral: boolean): number {
  if (!integral) return 4;
  const span = roundAway(domain.upper - domain.lower);
  return Math.max(2, Math.min(5, span + 1));
}

/* ── selection, the day's point, panning ─────────────────────────────────────────────────────── */

export function nearest(to: number, dates: readonly number[]): number | null {
  let best: number | null = null;
  for (const d of dates) if (best === null || Math.abs(d - to) < Math.abs(best - to)) best = d;
  return best;
}

export function daily(points: readonly Point[], agg: Aggregation): Point[] {
  const order: number[] = [];
  const firstSeen = new Map<number, number>();
  const acc = new Map<number, number>();
  for (const p of points) {
    const day = startOfDay(p.date);
    const cur = acc.get(day);
    if (cur === undefined) {
      order.push(day); firstSeen.set(day, p.date); acc.set(day, p.value);
    } else {
      acc.set(day, agg === "peak" ? Math.max(cur, p.value) : cur + p.value);
      if (p.date < (firstSeen.get(day) ?? p.date)) firstSeen.set(day, p.date);
    }
  }
  return order.map((d) => ({ date: firstSeen.get(d) ?? d, value: acc.get(d) ?? 0 }))
    .sort((a, b) => a.date - b.date);
}

/* 🔴 W123 — HIS WORD, 2026-10-01 (the twin of `ProgressChartPlan.fullDomain`, pinned equal on the corpus): *"I want for
   each time duration chosen to take the whole space of the graph … the first point … even touching our vertical line left …
   the most right point to be where the horizontal lines end … Same for the all time … plus the function of sliding."*
   A day is its midnight; a FIXED range is exactly day(today − N) … day(today); `All` is day(first) … day(latest), a window of
   `ALL_WINDOW_DAYS` days sliding across it (at rest the latest day ON the right end, at its left-most the first logged day ON
   the axis line), shorter → edge to edge, under `ALL_SHORTEST_DAYS` → latest − 7 … latest. Calendar days throughout. */
export const ALL_WINDOW_DAYS = 60;
export const ALL_SHORTEST_DAYS = 7;

export function fullDomain(dates: readonly number[], rangeDays: number | null, now: number): Domain {
  if (rangeDays !== null) {
    const today = startOfDay(now);
    return { lower: addDays(today, -rangeDays), upper: today };
  }
  const last = startOfDay(dates.length === 0 ? now : Math.max(...dates));
  const first = startOfDay(dates.length === 0 ? last : Math.min(...dates));
  if (Math.round((last - first) / DAY) < ALL_SHORTEST_DAYS) return { lower: addDays(last, -ALL_SHORTEST_DAYS), upper: last };
  return { lower: first, upper: last };
}

/** W123 (A) — a point's x: its DAY's position (midnight), the x its label marks. */
export function plotX(date: number): number { return startOfDay(date); }

/** The span on screen at once: a fixed range its whole scale; `All` at most `ALL_WINDOW_DAYS` days of it. */
export function visibleLength(rangeDays: number | null, dates: readonly number[], now: number): number {
  const full = fullDomain(dates, rangeDays, now);
  const whole = full.upper - full.lower;
  return rangeDays !== null ? whole : Math.min(whole, full.upper - addDays(full.upper, -ALL_WINDOW_DAYS));
}

/** Only `All` slides, and only when the history is longer than its window. */
export function isScrollable(rangeDays: number | null, dates: readonly number[], now: number): boolean {
  if (rangeDays !== null) return false;
  const full = fullDomain(dates, null, now);
  return full.upper - full.lower > visibleLength(null, dates, now) + 1000;
}

/** `All`'s window's leading edge may rest anywhere in this: the first logged day … the rest position. */
export function scrollBounds(rangeDays: number | null, dates: readonly number[], now: number): Domain {
  const full = fullDomain(dates, rangeDays, now);
  if (rangeDays !== null) return { lower: full.lower, upper: full.lower };
  return { lower: full.lower, upper: Math.max(full.lower, addDays(full.upper, -ALL_WINDOW_DAYS)) };
}

export function scrollPosition(centering: number, rangeDays: number | null, dates: readonly number[], now: number): number {
  const b = scrollBounds(rangeDays, dates, now);
  if (rangeDays !== null) return b.lower;
  const wanted = addDays(startOfDay(centering), -(ALL_WINDOW_DAYS / 2));
  return Math.min(Math.max(wanted, b.lower), b.upper);
}

export function initialScrollPosition(rangeDays: number | null, dates: readonly number[], now: number): number {
  return scrollBounds(rangeDays, dates, now).upper;
}

/** The window on screen for a scroll position (clamped): a fixed range is its whole scale. */
export function visibleWindow(rangeDays: number | null, dates: readonly number[], now: number, scroll: number): Domain {
  const full = fullDomain(dates, rangeDays, now);
  if (rangeDays !== null) return full;
  const b = scrollBounds(null, dates, now);
  const lo = Math.min(Math.max(scroll, b.lower), b.upper);
  return { lower: lo, upper: Math.min(full.upper, lo + visibleLength(null, dates, now)) };
}

/** The headline's window in instants: a fixed range's days, today WHOLE; `All` none. */
export function summaryWindow(rangeDays: number | null, dates: readonly number[], now: number): Domain | null {
  if (rangeDays === null) return null;
  const full = fullDomain(dates, rangeDays, now);
  return { lower: full.lower, upper: addDays(full.upper, 1) - 1 };
}

/** F3 — the twin of `ProgressChartPlan.isShown`: a fixed range draws only sessions whose DAY is inside it, today included. */
export function isShown(date: number, rangeDays: number | null, dates: readonly number[], now: number): boolean {
  if (rangeDays === null) return true;
  const w = fullDomain(dates, rangeDays, now);
  const day = startOfDay(date);
  return day >= w.lower && day <= w.upper;
}

/** W123 — the twin of `ProgressChartPlan.windowed`: the window's points at day positions, plus a point ON each edge the
 *  history runs past (linear between its neighbours), so the curve reaches the edge and stops there. */
export function windowed(points: readonly Point[], window: Domain): Point[] {
  const byDay = points.map((p) => ({ date: plotX(p.date), value: p.value })).sort((a, b) => a.date - b.date);
  const out = byDay.filter((p) => p.date >= window.lower && p.date <= window.upper);
  const edge = (at: number): Point | null => {
    const before = [...byDay].reverse().find((p) => p.date < at), after = byDay.find((p) => p.date > at);
    if (before === undefined || after === undefined) return null;
    return { date: at, value: before.value + ((after.value - before.value) * (at - before.date)) / (after.date - before.date) };
  };
  if (out[0]?.date !== window.lower) { const e = edge(window.lower); if (e !== null) out.unshift(e); }
  if (out[out.length - 1]?.date !== window.upper) { const e = edge(window.upper); if (e !== null) out.push(e); }
  return out;
}

/** `All` is labelled by its VISIBLE window, never by the whole history. */
export function labelSpanDays(rangeDays: number | null, tenure: number): number {
  return rangeDays === null ? Math.min(Math.max(tenure, ALL_SHORTEST_DAYS), ALL_WINDOW_DAYS) : tenure;
}

/* ── the headline ────────────────────────────────────────────────────────────────────────────── */

export type Summary = { value: number; date: number; delta: number | null; comparedSessions: number };

export function summary(points: readonly Point[], selected: number | null, window: Domain | null): Summary | null {
  let pool: readonly Point[] = points;
  if (window !== null) {
    const inWindow = points.filter((p) => p.date >= window.lower && p.date <= window.upper);
    if (inWindow.length > 0) pool = inWindow;
  }
  const first = pool[0], last = pool[pool.length - 1];
  if (first === undefined || last === undefined) return null;
  let chosen = last;
  if (selected !== null) {
    const hits = points.filter((p) => sameDay(p.date, selected));
    const hit = hits[hits.length - 1];
    if (hit !== undefined) chosen = hit;
  }
  const delta = sameDay(chosen.date, first.date) ? null : chosen.value - first.value;
  return { value: chosen.value, date: chosen.date, delta, comparedSessions: pool.length };
}

/* ── ProgressRange (moved into ProgressChartPlan.swift so the harness compiles it) ───────────── */

export const RANGES = ["week", "twoWeeks", "month", "threeMonths", "sixMonths", "year", "all"] as const;
export type Range = (typeof RANGES)[number];

export function rangeDays(r: Range): number | null {
  switch (r) {
    case "week": return 7;
    case "twoWeeks": return 14;
    case "month": return 30;
    case "threeMonths": return 90;
    case "sixMonths": return 180;
    case "year": return 365;
    case "all": return null;
  }
}

export function rangeLongLabel(r: Range): string {
  switch (r) {
    case "week": return "1 Week";
    case "twoWeeks": return "2 Weeks";
    case "month": return "1 Month";
    case "threeMonths": return "3 Months";
    case "sixMonths": return "6 Months";
    case "year": return "1 Year";
    case "all": return "All time";
  }
}

export function isUnlocked(r: Range, tenureDays: number): boolean {
  if (r === "week" || r === "all") return true;
  return tenureDays >= (rangeDays(r) ?? 0);
}

/** A stored range this exercise's span cannot fill falls back to the week — the one window never refused. */
export function resolvedRange(stored: Range | null, tenureDays: number): Range {
  const r = stored ?? "week";
  return isUnlocked(r, tenureDays) ? r : "week";
}

/* ── the series, the record, the words ───────────────────────────────────────────────────────── */

/** What `activeSeries` reads off an entry — the chart only needs these. */
export type ChartEntry = {
  readonly date: number;
  readonly topWeightKg: number | null;
  readonly bestReps: number | null;
  readonly bestHoldSeconds: number | null;
  readonly volumeKg: number;
  readonly bestE1RMKg: number | null;
  readonly performed: readonly { readonly reps: number | null; readonly holdSeconds: number | null;
                                  readonly distanceM?: number | null; readonly rounds?: number | null }[];
  /** W125 — the sets the exact 1RM reads (`oneRepMax.ts`). */
  readonly oneRMSets?: readonly OneRMSet[];
};

/** W125 — `ProgressChartPlan.fields(for:oneRM:)`: whether any day can show a 1RM under the exercise's rule. */
function hasOneRM(entries: readonly ChartEntry[], rule: OneRMRule): boolean {
  if (rule.kind === "engine") return entries.some((e) => (e.bestE1RMKg ?? 0) > 0);
  if (!rule.enabled) return false;
  return entries.some((e) => (e.oneRMSets ?? []).some((s) => feeds(s, rule.bodyIsLoad)));
}

/** W125 — `ProgressChartPlan.oneRMNeedsBodyweight`: the body is part of the load, a set could feed it, nobody weighed in. */
export function oneRMNeedsBodyweight(entries: readonly ChartEntry[], rule: OneRMRule, weighIns: readonly WeighIn[]): boolean {
  return ruleBodyIsLoad(rule) && !weighIns.some((w) => w.kg > 0)
    && entries.some((e) => (e.oneRMSets ?? []).some((s) => feeds(s, true)));
}

export function fieldsFor(entries: readonly ChartEntry[], rule: OneRMRule = ENGINE_RULE): Field[] {
  return availableFields(
    hasOneRM(entries, rule),
    entries.some((e) => (e.topWeightKg ?? 0) > 0),
    entries.some((e) => (e.bestReps ?? 0) > 0),
    entries.some((e) => (e.bestHoldSeconds ?? 0) > 0),
    entries.some((e) => e.performed.some((p) => (p.distanceM ?? 0) > 0)),
    entries.some((e) => e.performed.some((p) => (p.rounds ?? 0) > 0)),
  );
}

export function tenureDays(entries: readonly ChartEntry[]): number {
  const first = entries[0], last = entries[entries.length - 1];
  if (first === undefined || last === undefined) return 0;
  return wholeDaysBetween(first.date, last.date);
}

/** `ProgressChartPlan.points(field:entries:)` — one point per DAY, collapsed by the field's own meaning. */
export function seriesPoints(field: Field, entries: readonly ChartEntry[], rule: OneRMRule = ENGINE_RULE,
                             weighIns: readonly WeighIn[] = []): Point[] {
  const raw: Point[] = [];
  for (const e of entries) {
    let v: number | null = null;
    switch (field) {
      // W125: a coach's exercise reads the EXACT estimate (`oneRepMax.ts`, generated from the Swift); an engine one, Epley.
      case "oneRM": v = rule.kind === "coach"
        ? (rule.enabled ? bestOf(e.oneRMSets ?? [], e.date, rule, weighIns) : null)
        : ((e.bestE1RMKg ?? 0) > 0 ? e.bestE1RMKg : null); break;
      case "topSet": v = (e.topWeightKg ?? 0) > 0 ? e.topWeightKg : null; break;
      case "volume": v = e.volumeKg > 0 ? e.volumeKg : null; break;
      case "bestSet": v = (e.bestReps ?? 0) > 0 ? e.bestReps : null; break;
      case "totalReps": {
        const t = e.performed.reduce((s, p) => s + ((p.rounds ?? null) !== null ? 0 : (p.reps ?? 0)), 0);
        v = t > 0 ? t : null; break;
      }
      case "longestHold": v = (e.bestHoldSeconds ?? 0) > 0 ? e.bestHoldSeconds : null; break;
      case "timeUnderTension": {
        // W136 B2 S3r5 P2 (D7): holds only — a run's or a rounds set's typed time is not one.
        const t = e.performed.reduce((s, p) => s + ((p.distanceM ?? null) !== null || (p.rounds ?? null) !== null ? 0 : (p.holdSeconds ?? 0)), 0);
        v = t > 0 ? t : null; break;
      }
      case "longestDistance": {
        const best = Math.max(0, ...e.performed.map((p) => p.distanceM ?? 0));
        v = best > 0 ? best : null; break;
      }
      case "totalDistance": {
        const t = e.performed.reduce((s, p) => s + (p.distanceM ?? 0), 0);
        v = t > 0 ? t : null; break;
      }
      case "mostRounds": {
        const best = Math.max(0, ...e.performed.map((p) => p.rounds ?? 0));
        v = best > 0 ? best : null; break;
      }
    }
    if (v !== null) raw.push({ date: e.date, value: v });
  }
  return daily(raw, aggregation(field));
}

/** The record: the best value over the WHOLE history, and the FIRST day it was reached. */
export function personalRecord(points: readonly Point[]): Point | null {
  if (points.length === 0) return null;
  const best = Math.max(...points.map((p) => p.value));
  return points.find((p) => p.value === best) ?? null;
}

/** `shortNumber` / `valueText`'s number: whole numbers bare, else one decimal (printf `%.1f`, ties to even). */
export function numberText(v: number): string {
  if (Number.isInteger(v)) return String(v);
  /* Both printf and `toFixed` round the EXACT binary value; they differ only on an exact tie, which for one decimal
     exists only at x.25 and x.75 (a double holds k/20 exactly only when it is k/4). printf breaks those to EVEN,
     `toFixed` upward — so the tie is decided here and everything else is left to `toFixed`. */
  const exactTie = Number.isInteger(v * 4) && !Number.isInteger(v * 2);
  if (exactTie) {
    const tenths = Math.floor(v * 10);            // x.25 → x.2 (even), x.75 → x.7 → x.8
    return ((tenths % 2 === 0 ? tenths : tenths + 1) / 10).toFixed(1);
  }
  return v.toFixed(1);
}

export function valueText(v: number, unit: string): string {
  return `${numberText(v)} ${unit}`;
}

/** `31/8` — day, slash, month. */
export function dayMonth(ms: number): string {
  const d = new Date(ms);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

/** `21/08/26`. */
export function dayMonthYear(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${p(d.getFullYear() % 100)}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function axisLabel(ms: number, rule: LabelRule): string {
  return rule.kind === "months" ? MONTHS[new Date(ms).getMonth()] ?? "" : dayMonth(ms);
}

export function axisUnitLabel(unit: string): string {
  return unit === "s" ? "sec" : unit;
}

export function prLine(points: readonly Point[], unit: string): string | null {
  const pr = personalRecord(points);
  return pr === null ? null : `PR ${valueText(pr.value, unit)} · ${dayMonthYear(pr.date)}`;
}

/** The caption under the big number. */
export function headlineCaption(field: Field, s: Summary, selected: number | null): string {
  if (selected === null) {
    return `${fieldView(field).label} · latest of ${s.comparedSessions} ${s.comparedSessions === 1 ? "session" : "sessions"}`;
  }
  return weekdayDate(localDayKey(new Date(s.date)));
}

/** `Mon 7 Sep` for an instant, cut in the page's zone — the readout's day and the lift row's "last done". */
export function sessionDay(ms: number): string {
  return shortDate(localDayKey(new Date(ms)));
}

/** The hint under the picker row when nothing is selected. */
export function hint(sessions: number): string {
  return sessions < 2 ? "Log one more session and this becomes a graph." : "Slide across the chart to read a session";
}

/** Dots up to a month (his rule) AND on `All`, whose window is two months since W121; 3M/6M/1Y keep the line. */
export function showsDots(rangeDays_: number | null): boolean {
  return rangeDays_ === null || rangeDays_ <= 30;
}
